import type React from 'react';
import { clientRectToLayoutRect } from './pageZoom';

export const SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX = 58;
export const SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX = 96;
/** 도형 블럭 미니 툴바: 스타일 1행 + 텍스트(폰트 크기~) 1행 */
export const SLIDE_TOOLBAR_SHAPE_TWO_ROW_HEIGHT_PX = 112;
/** 소스코드 블럭 미니 툴바: 코드 입력 + 옵션 행 */
export const SLIDE_TOOLBAR_CODE_BLOCK_HEIGHT_PX = 220;
export const SLIDE_TOOLBAR_EDGE_GAP_PX = 8;
export const SLIDE_TOOLBAR_INSIDE_INSET_PX = 8;

export type SlideToolbarPlacement = {
  className: string;
  style: React.CSSProperties;
};

export type SlideToolbarPlacementOptions = {
  /** true면 블럭 안(is-inside) 대신 아래(is-below)로 — 썸네일과 겹치지 않음 */
  disallowInside?: boolean;
};

/** 미니 툴바: 왼쪽 상단 기준점 — 위·아래 캔버스 밖이면 블럭 안쪽 왼쪽 상단(또는 disallowInside 시 아래) */
export function resolveSlideToolbarPlacement(
  blockY: number,
  blockH: number,
  toolbarHeightPx: number,
  canvasHeightPx: number,
  options?: SlideToolbarPlacementOptions
): SlideToolbarPlacement {
  const gap = SLIDE_TOOLBAR_EDGE_GAP_PX;
  const blockTopPx = blockY * canvasHeightPx;
  const blockBottomPx = (blockY + blockH) * canvasHeightPx;
  const need = toolbarHeightPx + gap;

  const canPlaceAbove = blockTopPx >= need;
  const canPlaceBelow = canvasHeightPx - blockBottomPx >= need;

  let placementClass = '';
  if (!canPlaceAbove && !canPlaceBelow) {
    placementClass = options?.disallowInside ? ' is-below' : ' is-inside';
  } else if (!canPlaceAbove) {
    placementClass = ' is-below';
  }

  return {
    className: placementClass,
    style: { ['--slide-toolbar-h' as string]: `${toolbarHeightPx}px` } as React.CSSProperties,
  };
}

/** 캔버스 좌표(%) 기준 플로팅 툴바 top — 블럭 자식이 아닐 때 사용 */
export function floatedToolbarTopStyle(
  blockY: number,
  blockH: number,
  placementClass: string
): React.CSSProperties {
  const gap = SLIDE_TOOLBAR_EDGE_GAP_PX;
  if (placementClass.includes('is-below') || placementClass.includes('is-inside')) {
    if (placementClass.includes('is-inside')) {
      return { top: `calc(${blockY * 100}% + ${SLIDE_TOOLBAR_INSIDE_INSET_PX}px)` };
    }
    return { top: `calc(${(blockY + blockH) * 100}% + ${gap}px)` };
  }
  return { top: `calc(${blockY * 100}% - var(--slide-toolbar-h, 58px) - ${gap}px)` };
}

function toolbarHeightFromPlacement(placement: SlideToolbarPlacement): number {
  const raw = placement.style['--slide-toolbar-h' as keyof React.CSSProperties];
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX;
}

export type FloatedToolbarVerticalPlacement = 'above' | 'below' | 'inside';

function verticalPlacementFromClass(placementClass: string): FloatedToolbarVerticalPlacement {
  if (placementClass.includes('is-inside')) return 'inside';
  if (placementClass.includes('is-below')) return 'below';
  return 'above';
}

function classNameFromVerticalPlacement(vertical: FloatedToolbarVerticalPlacement): string {
  if (vertical === 'below') return ' is-below';
  if (vertical === 'inside') return ' is-inside';
  return '';
}

function resolveFloatedToolbarInBounds(input: {
  boundsLeft: number;
  boundsTop: number;
  boundsRight: number;
  boundsBottom: number;
  blockLeft: number;
  blockTop: number;
  blockBottom: number;
  panelWidth: number;
  panelHeight: number;
  preferredPlacementClass: string;
  gap: number;
}): { top: number; left: number; placementClass: string } {
  const { panelWidth, panelHeight, gap } = input;
  const { boundsLeft, boundsTop, boundsRight, boundsBottom } = input;
  const { blockLeft, blockTop, blockBottom } = input;

  const topFor = (vertical: FloatedToolbarVerticalPlacement) => {
    if (vertical === 'inside') return blockTop + SLIDE_TOOLBAR_INSIDE_INSET_PX;
    if (vertical === 'below') return blockBottom + gap;
    return blockTop - panelHeight - gap;
  };

  const fitsVertically = (vertical: FloatedToolbarVerticalPlacement) => {
    const top = topFor(vertical);
    return top >= boundsTop && top + panelHeight <= boundsBottom;
  };

  let vertical = verticalPlacementFromClass(input.preferredPlacementClass);
  if (!fitsVertically(vertical)) {
    const opposite: FloatedToolbarVerticalPlacement =
      vertical === 'above' ? 'below' : vertical === 'below' ? 'above' : 'below';
    if (fitsVertically(opposite)) {
      vertical = opposite;
    } else {
      for (const candidate of ['below', 'above', 'inside'] as const) {
        if (fitsVertically(candidate)) {
          vertical = candidate;
          break;
        }
      }
    }
  }

  let top = topFor(vertical);
  top = Math.min(Math.max(top, boundsTop), Math.max(boundsTop, boundsBottom - panelHeight));

  let left = blockLeft;
  if (left + panelWidth > boundsRight) {
    left = boundsRight - panelWidth;
  }
  if (left < boundsLeft) {
    left = boundsLeft;
  }

  return { top, left, placementClass: classNameFromVerticalPlacement(vertical) };
}

/** viewport portal 툴바 — 컨테이너(평가지 모달) 안에 맞추고 넘치면 반대 방향 */
export function resolveFloatedToolbarInContainer(input: {
  containerRect: DOMRect;
  canvasRect: DOMRect;
  blockX: number;
  blockY: number;
  blockH: number;
  panelWidth: number;
  panelHeight: number;
  preferredPlacementClass: string;
  gap?: number;
  margin?: number;
}): { top: number; left: number; placementClass: string } {
  const gap = input.gap ?? SLIDE_TOOLBAR_EDGE_GAP_PX;
  const margin = input.margin ?? 8;
  const { blockX, blockY, blockH, panelWidth, panelHeight } = input;
  const containerRect = clientRectToLayoutRect(input.containerRect);
  const canvasRect = clientRectToLayoutRect(input.canvasRect);

  return resolveFloatedToolbarInBounds({
    boundsLeft: containerRect.left + margin,
    boundsTop: containerRect.top + margin,
    boundsRight: containerRect.right - margin,
    boundsBottom: containerRect.bottom - margin,
    blockLeft: canvasRect.left + blockX * canvasRect.width,
    blockTop: canvasRect.top + blockY * canvasRect.height,
    blockBottom: canvasRect.top + (blockY + blockH) * canvasRect.height,
    panelWidth,
    panelHeight,
    preferredPlacementClass: input.preferredPlacementClass,
    gap,
  });
}

/** 캔버스 host portal — host 로컬 좌표에서 화면(호스트) 밖으로 나가지 않게 클램프 */
export function resolveFloatedToolbarInHost(input: {
  hostWidth: number;
  hostHeight: number;
  blockX: number;
  blockY: number;
  blockH: number;
  panelWidth: number;
  panelHeight: number;
  preferredPlacementClass: string;
  gap?: number;
  margin?: number;
}): { top: number; left: number; placementClass: string } {
  const gap = input.gap ?? SLIDE_TOOLBAR_EDGE_GAP_PX;
  const margin = input.margin ?? 8;
  const hostW = Math.max(1, input.hostWidth);
  const hostH = Math.max(1, input.hostHeight);
  return resolveFloatedToolbarInBounds({
    boundsLeft: margin,
    boundsTop: margin,
    boundsRight: hostW - margin,
    boundsBottom: hostH - margin,
    blockLeft: input.blockX * hostW,
    blockTop: input.blockY * hostH,
    blockBottom: (input.blockY + input.blockH) * hostH,
    panelWidth: input.panelWidth,
    panelHeight: input.panelHeight,
    preferredPlacementClass: input.preferredPlacementClass,
    gap,
  });
}

/** 표·패널 — 앵커 오른쪽 기본, 컨테이너 밖이면 왼쪽(반대) */
export function resolveSidePanelInContainer(input: {
  containerRect: DOMRect;
  anchorRect: DOMRect;
  panelWidth: number;
  panelHeight: number;
  margin?: number;
  gap?: number;
}): { top: number; left: number } {
  const margin = input.margin ?? 12;
  const gap = input.gap ?? margin;
  const { panelWidth, panelHeight } = input;
  const containerRect = clientRectToLayoutRect(input.containerRect);
  const anchorRect = clientRectToLayoutRect(input.anchorRect);

  const boundsTop = containerRect.top + margin;
  const boundsBottom = containerRect.bottom - margin;
  const boundsLeft = containerRect.left + margin;
  const boundsRight = containerRect.right - margin;

  let left = anchorRect.right + gap;
  if (left + panelWidth > boundsRight) {
    left = anchorRect.left - panelWidth - gap;
  }
  left = Math.min(Math.max(left, boundsLeft), Math.max(boundsLeft, boundsRight - panelWidth));

  let top = anchorRect.top;
  if (top + panelHeight > boundsBottom) {
    top = boundsBottom - panelHeight;
  }
  if (top < boundsTop) {
    top = boundsTop;
  }

  return { top, left };
}

/** body fixed portal — 캔버스 viewport rect + 블럭 정규 좌표 */
export function buildFloatedToolbarViewportStyle(
  canvasRect: DOMRect,
  blockX: number,
  blockY: number,
  blockH: number,
  placement: SlideToolbarPlacement,
  zIndex: number
): React.CSSProperties {
  const gap = SLIDE_TOOLBAR_EDGE_GAP_PX;
  const toolbarH = toolbarHeightFromPlacement(placement);
  const canvasLayout = clientRectToLayoutRect(canvasRect);
  const left = canvasLayout.left + blockX * canvasLayout.width;
  const blockTop = canvasLayout.top + blockY * canvasLayout.height;
  const blockBottom = canvasLayout.top + (blockY + blockH) * canvasLayout.height;
  let top: number;
  const cls = placement.className;
  if (cls.includes('is-inside')) {
    top = blockTop + SLIDE_TOOLBAR_INSIDE_INSET_PX;
  } else if (cls.includes('is-below')) {
    top = blockBottom + gap;
  } else {
    top = blockTop - toolbarH - gap;
  }
  return {
    position: 'fixed',
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    zIndex,
    pointerEvents: 'auto',
    ...placement.style,
  };
}
